import { isBefore } from "date-fns";
import { action, computed, flow, observable } from "mobx";
import { observer, Observer } from "mobx-react";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { QueryClient, useQueryClient } from "react-query";
import styled, { createGlobalStyle } from "styled-components";
import { GetTypesResponse, Type } from "../../../../api/types/getTypes";
import { getPanelScrollHeight } from "../../../../Components/getPanelScrollHeight";
import { useCustomAlignDropdownOverlay } from "../../../../Components/useCustomAlignOverlay";
import { getLocStore, useLocStore } from "../../../../wsLocalization";
import { LocContext } from "../../../../wsLocalization/LocContext";
import DomHandler from "../../../../WSUtils/DomHandler";
import {
  GenericDialogActions,
  invalidFieldsCannotBeSavedDialog,
  unsavedChangesDialog,
} from "../../../../WSUtils/genericDialogs";
import { scrollIntoView } from "../../../../WSUtils/scrollIntoView";
import { addUnsavedChange } from "../../../../WSUtils/unsavedChanges";
import { redirect } from "../../../../WSUtils/urlUtils";
import { useLatestQuery } from "../../../../WSUtils/useLatestQuery";
import { concatClassnames } from "../../../../WSUtils/util";
import { WSFormGrid } from "../../../../WSUtils/WSFormGrid";
import {
  CVPSearchParams,
  queryStringStore,
} from "../../../../WSUtils/wsQueryStringStore";
import { sentryAssertData } from "../../../../WSUtils/wsSentry";
import { CVEditStepType } from "../../../Steps/Step";
import { CVEditContext } from "../../CVEditContext";
import { CVEditStore, CVEditStoreParams } from "../../CVEditStore";
import {
  CVEditBaseField,
  cveField,
  cveFieldStore,
  OnRenderCvEditComponent,
} from "../../Fields/CVEditBaseField";
import { CVEditExperience } from "./CVEditExperience";
import { saveCurrentlyEditingExperienceAndCloseEditor } from "./CVEditExperienceSegment";
import { CVEditExperienceSegmentStore } from "./CVEditExperienceSegmentStore";

export interface CVEditExperienceEditorProps {
  experienceSegmentStore: CVEditExperienceSegmentStore;
}
export const CVEditExperienceEditor = ({
  experienceSegmentStore,
}: CVEditExperienceEditorProps) => {
  const L = useLocStore(LocContext.CV);
  const cvEditContext = useContext(CVEditContext);
  const queryClient = useQueryClient();

  const savingUsingCloseButton = useMemo(() => observable.box(false), []);

  useLayoutEffect(() => {
    scrollIntoView(document.querySelector(".cve-experience-editor"));
  }, [experienceSegmentStore.editingExperienceId]);

  return (
    <Observer
      render={() => {
        const expEditorStore = experienceSegmentStore.experienceEditorStore;

        if (!expEditorStore) return null;

        return (
          <CVEditExperienceEditorStyles
            margin="1.15rem"
            float
            className="cve-experience-editor cve-x-editor"
          >
            <div className="cvp-h3 cve-smaller-header">
              {!!expEditorStore.cveExp.isNew &&
                L.id("c590a20ffebc").loc("Ny arbeidserfaring")}
              {!expEditorStore.cveExp.isNew &&
                L.id("6b8fa3bda52c").loc("Rediger arbeidserfaring")}
              <Button
                icon="pi pi-times"
                className={concatClassnames([
                  "p-button-text p-button-icon-only p-button-rounded",
                  "cve-editor-cancel",
                ])}
                loading={savingUsingCloseButton.get()}
                onClick={flow(function* (evt) {
                  evt.preventDefault();

                  onCveExperienceEditorCloseButtonClick({
                    experienceSegmentStore,
                    expEditorStore: expEditorStore,
                    queryClient,
                    onSaveApiCallStart: () => {
                      savingUsingCloseButton.set(true);
                    },
                    onSaveApiCallEnd: () => {
                      savingUsingCloseButton.set(false);
                    },
                  });

                  return false;
                })}
              />
            </div>

            <CVEditBaseField def={expEditorStore.fields.employerField} />

            <WSFormGrid horizontal>
              <CVEditBaseField
                def={expEditorStore.fields.fromDateField}
                containerProps={{ style: { flexGrow: 1 } }}
              />
              <CVEditBaseField
                def={expEditorStore.fields.toDateField}
                containerProps={{ style: { flexGrow: 1 } }}
              />
            </WSFormGrid>

            <CVEditBaseField
              def={expEditorStore.fields.workingHereNowField}
              containerProps={{
                className: "cve-working-here-now-checkbox",
              }}
            />
            <CVEditBaseField def={expEditorStore.fields.positionField} />
            <CVEditBaseField def={expEditorStore.fields.industryTypeField} />
            <CVEditBaseField def={expEditorStore.fields.descriptionField} />
          </CVEditExperienceEditorStyles>
        );
      }}
    />
  );
};

const CVEditExperienceEditorStyles = styled(WSFormGrid)`
  &.cve-experience-editor {
  }
`;

export interface OnCveExperienceEditorCloseButtonClickParams {
  expEditorStore: CVEditExperienceEditorStore;
  queryClient: QueryClient;
  onSaveApiCallStart: () => any;
  onSaveApiCallEnd: () => any;
  experienceSegmentStore: CVEditExperienceSegmentStore;
}

export const onCveExperienceEditorCloseButtonClick = flow(function* ({
  expEditorStore,
  queryClient,
  onSaveApiCallStart,
  onSaveApiCallEnd,
  experienceSegmentStore,
}: OnCveExperienceEditorCloseButtonClickParams) {
  const userInfo = expEditorStore.userInfo;
  const cvEditContext = expEditorStore.cvEditContext;

  const noChanges = expEditorStore.unsavedChanges.length === 0;
  const noErrors = expEditorStore.validationErrors.length === 0;
  const isNew = expEditorStore.cveExp.isNew;

  if (noChanges && (noErrors || isNew)) {
    expEditorStore.closeEditor();
    return;
  }

  let hasChanges = !noChanges;
  let hasErrors = !noErrors;

  let action;
  if (hasChanges) {
    action = yield unsavedChangesDialog(
      userInfo,
      this.L.id("0e6073310ef6").loc("Lagre endret arbeidserfaring?")
    );
  } else if (hasErrors) {
    // this can happen if we change validation requirements
    action = yield invalidFieldsCannotBeSavedDialog(userInfo);
  }

  if (action === GenericDialogActions.SAVE_CHANGES) {
    try {
      onSaveApiCallStart();
      yield saveCurrentlyEditingExperienceAndCloseEditor({
        userInfo,
        cvEditContext,
        queryClient,
        experienceSegmentStore,
      });
    } finally {
      onSaveApiCallEnd();
    }
  } else if (action === GenericDialogActions.DISCARD_CHANGES) {
    expEditorStore.closeEditor();
  }

  expEditorStore.hasAttemptedToClose = true;
});

export interface CVEditExperienceEditorStoreParams extends CVEditStoreParams {
  cveExp: CVEditExperience;
}

export class CVEditExperienceEditorStore extends CVEditStore {
  constructor(params: CVEditExperienceEditorStoreParams) {
    super(params);

    const needsConfirmationBeforeLeaving = () => {
      const isViewingThis =
        queryStringStore.qs[CVPSearchParams.cvpee] === this.cveExp.id &&
        this.cvEditContext.currentStep.value === CVEditStepType.Experience;

      if (!isViewingThis) return false;
      if (this.isTerminated) return false;
      return this.unsavedChanges.length > 0;
    };

    this.dt.disposers.push(
      addUnsavedChange({
        needsConfirmationBeforeUnload: needsConfirmationBeforeLeaving,
        needsConfirmationBeforeSpaRedirect: needsConfirmationBeforeLeaving,
      })
    );
  }

  @computed get L() {
    return getLocStore(this.userInfo, LocContext.CV);
  }

  cveExp: CVEditExperience;

  @action closeEditor = () => {
    this.isTerminated = true;
    redirect(null, {
      userInfo: this.userInfo,
      mergeQs: { [CVPSearchParams.cvpee]: null },
    });
  };

  @observable isTerminated = false;

  @observable hasAttemptedToClose = false;

  @computed get fields() {
    return {
      descriptionField: this.descriptionField,
      employerField: this.employerField,
      fromDateField: this.fromDateField,
      toDateField: this.toDateField,
      industryTypeField: this.industryTypeField,
      positionField: this.positionField,
      workingHereNowField: this.workingHereNowField,
    };
  }

  @computed get validationErrors() {
    return Object.values(this.fields).filter((f) => f.store.validationError);
  }

  @computed get visibleValidationErrors() {
    return Object.values(this.fields).filter(
      (f) => f.store.visibleValidationError
    );
  }

  @computed get unsavedChanges(): [string, any, any][] {
    const result = Object.values(this.fields)
      .map((f) => {
        const result = f.hasUnsavedChanges();

        if (!result) return false;

        const [name, val1, val2] = result;

        if (val1 === val2) return false;

        return [name, val1, val2];
      })
      .filter((f) => f);

    return result as any;
  }

  @observable workingHereNowField = cveField(() => {
    const _this = this;
    const userInfo = this.userInfo;

    const store = cveFieldStore({
      value: !this.cveExp.to,
      get validationError(): string {
        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "WorkingHereNow",
      getLabel: () => this.L.id("630ad44a3b4a").loc("Jeg jobber her nå"),
      hasUnsavedChanges: () => false,
      onRenderComponent: ({ def }) => (
        <Observer
          render={() => {
            return (
              <Checkbox
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                inputId={def.id}
                name={def.id}
                checked={store.value}
                onChange={action((evt) => {
                  store.value = !!evt.checked;
                  store.onChangeCount++;

                  if (store.value) {
                    this.toDateField.store.value = null;
                    this.toDateField.store.onChangeCount++;
                  }
                })}
              />
            );
          }}
        />
      ),
    };
  });

  @observable descriptionField = cveField(() => {
    const _this = this;
    const store = cveFieldStore({
      value: this.cveExp.description || "",
      get validationError(): string {
        if (store.value.length > 1010) {
          return _this.L.id("7335c81d33b2").loc("Maks antall tegn er 1010");
        }

        return "";
      },
      get showValidationError(): boolean {
        return (
          store.onChangeCount > 0 ||
          _this.hasAttemptedToClose ||
          store.value.length > 1010
        );
      },
    });

    return {
      store,
      id: "Description",
      getLabel: () => this.L.id("fd7c8610d7be").loc("Arbeidsoppgaver"),
      hasUnsavedChanges: () => [
        "Description",
        this.cveExp.description,
        store.value,
      ],
      onRenderComponent: ({ def }) => (
        <Observer
          render={() => {
            return (
              <InputTextarea
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                name={def.id}
                id={def.id}
                value={store.value}
                onChange={action((evt) => {
                  store.value = evt.target.value;
                })}
                rows={4}
              />
            );
          }}
        />
      ),
    };
  });

  @observable industryTypeField = cveField(() => {
    const userInfo = this.userInfo;
    const _this = this;

    const store = cveFieldStore({
      value: this.cveExp.industryType,

      get validationError(): string {
        if (!store.value) {
          return _this.L.id("389156031e74").loc("Dette feltet er påkrevd");
        }

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "Industry",
      getLabel: () => this.L.id("c6a4f97fdbb2").loc("Bransje"),
      hasUnsavedChanges: () => [
        "Industry",
        this.cveExp.industryType,
        store.value,
      ],
      onRenderComponent: observer<OnRenderCvEditComponent>(({ def }) => {
        const { data: bransjer } = useLatestQuery(
          ["typesAPI.getTypes(bransjer)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo,
              group: "bransjer",
            })
        );

        sentryAssertData(bransjer, { userInfo });

        const ref = useCustomAlignDropdownOverlay();

        return (
          <Dropdown
            className={concatClassnames([
              store.visibleValidationError && "p-invalid",
            ])}
            ref={ref}
            inputId={def.id}
            name={def.id}
            value={store.value}
            scrollHeight={getPanelScrollHeight()}
            filter
            options={bransjer?.mapped}
            onChange={action((evt) => {
              let val = evt.value;

              if (val && typeof val === "object") {
                store.value = val.value;
              } else {
                store.value = val;
              }

              store.onChangeCount++;
            })}
          />
        );
      }),
    };
  });

  @observable positionField = cveField(() => {
    const userInfo = this.userInfo;
    const _this = this;

    const store = cveFieldStore({
      value: _this.cveExp.position,
      vikartyper: null as GetTypesResponse,
      searchString: "",
      newOptionsTrigger: 0,
      showAll: true,

      get validationError(): string {
        if (!store.value) {
          return _this.L.id("e2aaaadf9881").loc("Dette feltet er påkrevd");
        }

        return "";
      },

      get options() {
        if (!store.vikartyper) return [];

        return (store.vikartyper as GetTypesResponse).mapped.map((group) => ({
          label: group.label,
          children: group.children,
        }));
      },

      get searchStringResolved() {
        return store.searchString.toLowerCase();
      },

      get filteredOptions() {
        store.newOptionsTrigger; // make dropdown open with empty searchString

        if (store.showAll) {
          return store.options;
        }

        const matches: {
          label: string;
          children: Type[];
        }[] = [];

        store.options.forEach((group) => {
          if (labelMatches(group)) {
            matches.push(group);
            return;
          }

          const matchingChildren: Type[] = [];

          group.children.forEach((child) => {
            if (labelMatches(child)) {
              matchingChildren.push(child);
            }
          });

          if (matchingChildren.length > 0) {
            matches.push({
              label: group.label,
              children: matchingChildren,
            });
          }
        });

        return matches;
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    const labelMatches = (option: { label: string; children: Type[] }) => {
      return option.label.toLowerCase().includes(store.searchStringResolved);
    };

    return {
      store: store,
      id: "Position",
      getLabel: () => this.L.id("97b47aedbd0c").loc("Stilling"),
      hasUnsavedChanges: () => ["Position", this.cveExp.position, store.value],
      onRenderComponent: ({ def }) => {
        const L = useLocStore(LocContext.CV);
        const ref = useRef(null);
        const showing = useRef(false);
        const containerRef = useRef(false);

        const showOverlay = () => {
          if (ref.current) {
            // make panel target the custom container
            ref.current.alignOverlay = function () {
              DomHandler.alignOverlay(
                this.overlayRef.current,
                containerRef.current,
                null
              );
            };
          }

          const showOverlay =
            typeof ref.current?.showOverlay === "function" && !showing.current;

          if (showOverlay) {
            ref.current.showOverlay();
          }
        };

        const { data: vikartyper } = useLatestQuery(
          ["typesAPI.getTypes(vikartype)"],
          () =>
            userInfo.fullAPI.typesAPI.getTypes({
              userInfo,
              group: "vikartype",
              excludeFolderName: false,
            })
        );

        sentryAssertData(vikartyper, { userInfo });

        // put react-query data in mobx state
        useEffect(
          action(() => {
            store.vikartyper = vikartyper;
          }),
          [vikartyper]
        );

        return (
          <Observer
            render={() => (
              <PositionInputStyles
                className="p-inputgroup cve-position-input-group"
                ref={containerRef as any}
              >
                <PositionInputGlobalStyles />

                <button
                  className={
                    "p-button p-component " +
                    "cve-position-dropdown-button p-button-secondary"
                  }
                  onClick={action((evt) => {
                    evt.preventDefault();

                    store.showAll = true;
                    showOverlay();

                    return false;
                  })}
                >
                  <span className="p-button-label p-c">
                    {L.id("a85b76300075").loc("Velg\r\nstillingstype")}{" "}
                    <i className="pi pi-chevron-down"></i>
                  </span>
                </button>

                <AutoComplete
                  className={concatClassnames([
                    store.visibleValidationError && "p-invalid",
                  ])}
                  inputId={def.id}
                  name={def.id}
                  optionGroupLabel="label"
                  optionGroupChildren="children"
                  inputClassName="cve-position-input"
                  placeholder={L.id("2619d443af75").loc(
                    "Skriv her om du ikke finner passende type"
                  )}
                  minLength={0}
                  field="label"
                  scrollHeight={getPanelScrollHeight()}
                  suggestions={store.filteredOptions}
                  completeMethod={action((evt) => {
                    store.searchString = evt.query;
                    store.newOptionsTrigger++;
                  })}
                  value={store.value}
                  onChange={action((evt) => {
                    if (typeof evt.value === "string") {
                      store.value = evt.value;
                    }
                    if (typeof evt.value === "object") {
                      store.value = evt.value.label;
                    }

                    store.onChangeCount++;
                  })}
                  onKeyPress={action(() => {
                    store.showAll = false;
                  })}
                  ref={ref}
                  onFocus={showOverlay}
                  onShow={() => {
                    showing.current = true;
                  }}
                  onHide={action(() => {
                    showing.current = false;
                    store.showAll = false;
                  })}
                />
              </PositionInputStyles>
            )}
          />
        );
      },
    };
  });

  @observable employerField = cveField(() => {
    const _this = this;

    const store = cveFieldStore({
      value: this.cveExp.employer || "",
      get validationError(): string {
        if (!store.value) {
          return _this.L.id("3b4fc2f6d5e6").loc("Dette feltet er påkrevd");
        }

        if (store.value.length > 100) {
          return _this.L.id("a841d73de2fa").loc("Maks antall tegn er 100");
        }

        return "";
      },
      get showValidationError(): boolean {
        return (
          store.onChangeCount > 0 ||
          _this.hasAttemptedToClose ||
          store.value.length > 100
        );
      },
    });

    return {
      store,
      id: "Employer",
      getLabel: () => this.L.id("6d3cd3bc0736").loc("Arbeidsgiver"),
      hasUnsavedChanges: () => ["Employer", this.cveExp.employer, store.value],
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            return (
              <InputText
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                id={args.def.id}
                name={args.def.id}
                value={store.value}
                onChange={action((evt) => {
                  store.value = evt.target.value;
                  store.onChangeCount++;
                })}
              />
            );
          }}
        />
      ),
    };
  });

  @observable fromDateField = cveField(() => {
    const _this = this;

    const store = cveFieldStore({
      value: this.cveExp.from || null,
      get validationError(): string {
        if (!store.value)
          return _this.L.id("1d145564a7e4").loc("Dette feltet er påkrevd");

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "From",
      getLabel: () => this.L.id("ab3a50510a20").loc("Fra"),
      hasUnsavedChanges: () => [
        "From",
        this.cveExp.from?.getTime(),
        store.value?.getTime(),
      ],
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            return (
              <Calendar
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                inputId={args.def.id}
                name={args.def.id}
                value={store.value}
                view="month"
                dateFormat="mm.yy"
                onChange={(evt) => {
                  store.value = evt.value as Date;
                  store.onChangeCount++;
                }}
              />
            );
          }}
        />
      ),
    };
  });

  @observable toDateField = cveField(() => {
    const _this = this;

    const store = cveFieldStore({
      value: this.cveExp.to || null,
      get validationError(): string {
        const from = _this.fromDateField.store.value;
        const to = store.value;

        if (from && to && isBefore(to, from)) {
          return this.L.id("e06114bd928d").loc("Tildato må være etter fradato");
        }

        return "";
      },
      get showValidationError(): boolean {
        return store.onChangeCount > 0 || _this.hasAttemptedToClose;
      },
    });

    return {
      store,
      id: "To",
      getLabel: () => this.L.id("669fdcc882e3").loc("Til"),
      hasUnsavedChanges: () => [
        "To",
        this.cveExp.to?.getTime(),
        store.value?.getTime(),
      ],
      onRenderComponent: (args) => (
        <Observer
          render={() => {
            return (
              <Calendar
                className={concatClassnames([
                  store.visibleValidationError && "p-invalid",
                ])}
                inputId={args.def.id}
                name={args.def.id}
                value={
                  this.workingHereNowField.store.value ? null : store.value
                }
                view="month"
                dateFormat="mm.yy"
                disabled={this.workingHereNowField.store.value}
                inputClassName={concatClassnames([
                  this.workingHereNowField.store.value &&
                    "cve-disabled-datefield-input",
                ])}
                onChange={(evt) => {
                  store.value = evt.value as Date;
                  store.onChangeCount++;
                }}
              />
            );
          }}
        />
      ),
    };
  });
}

const PositionInputStyles = styled.div`
  &.cve-position-input-group {
    .cve-position-dropdown-button {
      white-space: pre-wrap;
      flex-shrink: 0;

      .pi {
        font-size: 0.75rem;
      }
    }
  }
`;

/** Was only able to make this work using global style */
const PositionInputGlobalStyles = createGlobalStyle`

  /* Was only able to make this work without commas */
  .cve-position-input::-webkit-input-placeholder {
    white-space: pre-wrap;
    position: absolute;
    width: calc(100% - 1.5rem);
  }

  .cve-position-input::-moz-placeholder {
    white-space: pre-wrap;
    position: absolute;
    width: calc(100% - 1.5rem);
  }

  .cve-position-input:-ms-input-placeholder {
    white-space: pre-wrap;
    position: absolute;
    width: calc(100% - 1.5rem);
  }

  .cve-position-input:-moz-placeholder {
    white-space: pre-wrap;
    position: absolute;
    width: calc(100% - 1.5rem);
  }

  

`;
